import { useState, useRef, useEffect, useCallback } from 'react';
import Checkbox from '@/components/ui/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faChartPie,
  faUpDown,
  faArrowTrendUp,
  faSpinner,
  faTrash,
  faPlus,
  faPaperPlane,
  faExclamationTriangle,
  faUsers,
  faBell,
  faEnvelope
} from '@/fontawesome';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import Modal from '@/components/ui/Modal';
import RecommendationStatsModal from '@/components/admin/recommendations/RecommendationStatsModal';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';
import type {
  ResendTableRow,
  AnyRecommendation,
  ResendNotificationsRequest
} from '@/modelsAPI/resend-notifications';
import type { RecommendationCreateStats } from '@/models';

// Generate a unique row ID
const generateRowId = (): string => {
  return `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Create an empty row
const createEmptyRow = (): ResendTableRow => ({
  id: generateRowId(),
  recommendationId: '',
  recommendation: null,
  loading: false,
  error: null,
  comment: ''
});

// Type configuration with explicit Tailwind classes
const typeConfig: Record<TradingRecommendationType, {
  label: string;
  icon: typeof faBullseye;
  selectedBorder: string;
  selectedBg: string;
  selectedIconText: string;
  selectedText: string;
}> = {
  CORE: {
    label: 'Kernstrategie',
    icon: faBullseye,
    selectedBorder: 'border-blue-500',
    selectedBg: 'bg-blue-50 dark:bg-blue-900/20',
    selectedIconText: 'text-blue-600 dark:text-blue-400',
    selectedText: 'text-blue-600 dark:text-blue-400'
  },
  EARNINGS: {
    label: 'Earnings',
    icon: faChartPie,
    selectedBorder: 'border-green-500',
    selectedBg: 'bg-green-50 dark:bg-green-900/20',
    selectedIconText: 'text-green-600 dark:text-green-400',
    selectedText: 'text-green-600 dark:text-green-400'
  },
  SWING: {
    label: 'Swing-Trade',
    icon: faUpDown,
    selectedBorder: 'border-purple-500',
    selectedBg: 'bg-purple-50 dark:bg-purple-900/20',
    selectedIconText: 'text-purple-600 dark:text-purple-400',
    selectedText: 'text-purple-600 dark:text-purple-400'
  },
  LONGTERM: {
    label: 'Langzeit',
    icon: faArrowTrendUp,
    selectedBorder: 'border-orange-500',
    selectedBg: 'bg-orange-50 dark:bg-orange-900/20',
    selectedIconText: 'text-orange-600 dark:text-orange-400',
    selectedText: 'text-orange-600 dark:text-orange-400'
  }
};

// Helper to get ticker from any recommendation type
const getTickerFromRecommendation = (rec: AnyRecommendation): string => {
  return rec.symbolTicker;
};

// Helper to check notification sent flags
const getNotificationFlags = (rec: AnyRecommendation) => {
  return {
    discord: rec.discordNotificationSent ?? false,
    telegram: rec.telegramNotificationSent ?? false,
    scn: rec.scnNotificationSent ?? false,
    push: rec.pushNotificationSent ?? false,
    freeUserMail: rec.freeUserMailNotificationSent ?? false,
    allUsersMail: rec.allUsersMailNotificationSent ?? false
  };
};

export default function ResendNotificationsPanel() {
  // Type selection
  const [selectedType, setSelectedType] = useState<TradingRecommendationType>('CORE');

  // Dynamic table rows
  const [rows, setRows] = useState<ResendTableRow[]>([createEmptyRow()]);

  // Discord messages
  const [discordPreMessage, setDiscordPreMessage] = useState('');
  const [discordPostMessage, setDiscordPostMessage] = useState('');

  // Notification flags (all true by default)
  const [sendTelegram, setSendTelegram] = useState(true);
  const [sendDiscord, setSendDiscord] = useState(true);
  const [sendSCN, setSendSCN] = useState(true);
  const [sendPush, setSendPush] = useState(true);
  const [sendMailToFreeUser, setSendMailToFreeUser] = useState(true);
  const [sendMailToAllUsers, setSendMailToAllUsers] = useState(true);

  // Dry run preview
  const [dryRunStats, setDryRunStats] = useState<RecommendationCreateStats | null>(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  // Confirmation & results modals
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [conflictingNotifications, setConflictingNotifications] = useState<string[]>([]);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [sendResults, setSendResults] = useState<RecommendationCreateStats | null>(null);

  // Loading state
  const [isSending, setIsSending] = useState(false);

  // Debounce refs
  const fetchDebounceRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const dryRunDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch recommendation by ID
  const fetchRecommendation = useCallback(async (rowId: string, recommendationId: string) => {
    if (recommendationId.trim() === '') {
      setRows(prev => prev.map(row =>
        row.id === rowId
          ? { ...row, recommendation: null, error: null, loading: false }
          : row
      ));
      return;
    }

    // Set loading state
    setRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, loading: true, error: null } : row
    ));

    try {
      let recommendation: AnyRecommendation;

      switch (selectedType) {
        case 'CORE':
          recommendation = await APIRoutes.getCoreRecommendation(recommendationId as never);
          break;
        case 'EARNINGS':
          recommendation = await APIRoutes.getEarningsRecommendation(recommendationId as never);
          break;
        case 'SWING':
          recommendation = await APIRoutes.getSwingTrade(recommendationId as never);
          break;
        case 'LONGTERM':
          recommendation = await APIRoutes.getLongTermTrade(recommendationId as never);
          break;
      }

      setRows(prev => prev.map(row =>
        row.id === rowId
          ? { ...row, recommendation, loading: false, error: null }
          : row
      ));
    } catch {
      setRows(prev => prev.map(row =>
        row.id === rowId
          ? { ...row, recommendation: null, loading: false, error: 'Nicht gefunden' }
          : row
      ));
    }
  }, [selectedType]);

  // Handle recommendation ID change with debounce
  const handleRecommendationIdChange = (rowId: string, value: string) => {
    // Update the input value immediately
    setRows(prev => prev.map(row =>
      row.id === rowId
        ? { ...row, recommendationId: value, recommendation: null, error: null }
        : row
    ));

    // Clear existing debounce for this row
    const existingTimeout = fetchDebounceRefs.current.get(rowId);
    if (existingTimeout !== undefined) {
      clearTimeout(existingTimeout);
    }

    // Set new debounce
    const timeout = setTimeout(() => {
      fetchRecommendation(rowId, value);
      fetchDebounceRefs.current.delete(rowId);
    }, 500);

    fetchDebounceRefs.current.set(rowId, timeout);
  };

  // Auto-add new empty row when last row has non-empty ID
  useEffect(() => {
    const lastRow = rows[rows.length - 1];
    if (lastRow !== undefined && lastRow.recommendationId.trim() !== '') {
      setRows(prev => [...prev, createEmptyRow()]);
    }
  }, [rows]);

  // Delete row
  const handleDeleteRow = (rowId: string) => {
    setRows(prev => {
      const filtered = prev.filter(row => row.id !== rowId);
      // Always maintain at least one row
      if (filtered.length === 0) {
        return [createEmptyRow()];
      }
      return filtered;
    });
  };

  // Handle type change - reset all rows
  const handleTypeChange = (type: TradingRecommendationType) => {
    setSelectedType(type);
    setRows([createEmptyRow()]);
    setDryRunStats(null);
    setDiscordPreMessage('');
    setDiscordPostMessage('');
  };

  // Get valid rows (rows with successfully fetched recommendations)
  const getValidRows = () => rows.filter(row => row.recommendation !== null);

  // Check if there are any errors (non-empty ID with fetch failure)
  const hasErrors = rows.some(row => row.recommendationId.trim() !== '' && row.error !== null);

  // Check if form is valid for sending
  const isFormValid = getValidRows().length > 0 && !hasErrors;

  // Build request payload
  const buildRequestPayload = (): ResendNotificationsRequest => {
    return {
      resends: getValidRows().map(row => ({
        id:      row.recommendationId,
        comment: row.comment.trim()
      })),
      discordPreMessage:  discordPreMessage.trim()  !== '' ? discordPreMessage.trim()  : null,
      discordPostMessage: discordPostMessage.trim() !== '' ? discordPostMessage.trim() : null,
      sendTelegram,
      sendDiscord,
      sendSCN,
      sendPush,
      sendMailToFreeUser,
      sendMailToAllUsers
    };
  };

  // Fetch dry run stats
  const fetchDryRunStats = useCallback(async () => {
    if (!isFormValid) {
      setDryRunStats(null);
      return;
    }

    setDryRunLoading(true);

    try {
      const request = buildRequestPayload();
      let response: { stats: RecommendationCreateStats };

      switch (selectedType) {
        case 'CORE':
          response = await APIRoutes.resendCoreRecommendations(request, true);
          break;
        case 'EARNINGS':
          response = await APIRoutes.resendEarningsRecommendations(request, true);
          break;
        case 'SWING':
          response = await APIRoutes.resendSwingTradeRecommendations(request, true);
          break;
        case 'LONGTERM':
          response = await APIRoutes.resendLongTermRecommendations(request, true);
          break;
      }

      setDryRunStats(response.stats);
    } catch {
      setDryRunStats(null);
    } finally {
      setDryRunLoading(false);
    }
  }, [isFormValid, selectedType, rows, discordPreMessage, discordPostMessage, sendTelegram, sendDiscord, sendSCN, sendPush, sendMailToFreeUser, sendMailToAllUsers]);

  // Debounced dry run fetch
  useEffect(() => {
    if (dryRunDebounceRef.current !== null) {
      clearTimeout(dryRunDebounceRef.current);
    }

    if (!isFormValid) {
      setDryRunStats(null);
      setDryRunLoading(false);
      return;
    }

    setDryRunLoading(true);

    dryRunDebounceRef.current = setTimeout(() => {
      fetchDryRunStats();
    }, 500);

    return () => {
      if (dryRunDebounceRef.current !== null) {
        clearTimeout(dryRunDebounceRef.current);
      }
    };
  }, [isFormValid, fetchDryRunStats]);

  // Check for conflicts
  const checkForConflicts = (): string[] => {
    const conflicts: string[] = [];
    const validRows = getValidRows();

    for (const row of validRows) {
      if (row.recommendation === null) continue;

      const flags = getNotificationFlags(row.recommendation);
      const ticker = getTickerFromRecommendation(row.recommendation);
      const conflictParts: string[] = [];

      if (sendDiscord && flags.discord) conflictParts.push('Discord');
      if (sendTelegram && flags.telegram) conflictParts.push('Telegram');
      if (sendSCN && flags.scn) conflictParts.push('SCN');
      if (sendPush && flags.push) conflictParts.push('Push');
      if (sendMailToFreeUser && flags.freeUserMail) conflictParts.push('Mail (Free)');
      if (sendMailToAllUsers && flags.allUsersMail) conflictParts.push('Mail (Alle)');

      if (conflictParts.length > 0) {
        conflicts.push(`${ticker} (${row.recommendationId}): ${conflictParts.join(', ')}`);
      }
    }

    return conflicts;
  };

  // Handle send button click
  const handleSendClick = () => {
    if (!isFormValid) return;

    const conflicts = checkForConflicts();
    if (conflicts.length > 0) {
      setConflictingNotifications(conflicts);
      setShowConfirmModal(true);
    } else {
      performSend();
    }
  };

  // Perform the actual send
  const performSend = async () => {
    setShowConfirmModal(false);
    setIsSending(true);

    try {
      const request = buildRequestPayload();
      let response: { stats: RecommendationCreateStats };

      switch (selectedType) {
        case 'CORE':
          response = await APIRoutes.resendCoreRecommendations(request, false);
          break;
        case 'EARNINGS':
          response = await APIRoutes.resendEarningsRecommendations(request, false);
          break;
        case 'SWING':
          response = await APIRoutes.resendSwingTradeRecommendations(request, false);
          break;
        case 'LONGTERM':
          response = await APIRoutes.resendLongTermRecommendations(request, false);
          break;
      }

      setSendResults(response.stats);
      setShowResultsModal(true);
    } catch (error) {
      handleAPIError(error, 'Fehler beim Senden der Benachrichtigungen');
    } finally {
      setIsSending(false);
    }
  };

  // Handle results modal close
  const handleResultsModalClose = () => {
    setShowResultsModal(false);
    setSendResults(null);
    showSuccessToast('Benachrichtigungen erfolgreich gesendet');
    // Reset form
    setRows([createEmptyRow()]);
    setDiscordPreMessage('');
    setDiscordPostMessage('');
    setDryRunStats(null);
  };

  // Handle comment change
  const handleCommentChange = (rowId: string, value: string) => {
    setRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, comment: value } : row
    ));
  };

  // Render notification badges
  const renderNotificationBadges = (rec: AnyRecommendation) => {
    const flags = getNotificationFlags(rec);

    return (
      <div className="flex gap-1 whitespace-pre">
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.discord) ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200" : "bg-gray-600 text-gray-500")}>
            Discord
        </span>
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.telegram) ? "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200" : "bg-gray-600 text-gray-500")}>
            Telegram
        </span>
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.scn) ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200" : "bg-gray-600 text-gray-500")}>
            SCN
        </span>
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.push) ? "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200" : "bg-gray-600 text-gray-500")}>
            Push
        </span>
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.freeUserMail) ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" : "bg-gray-600 text-gray-500")}>
            FreeMail
        </span>
        <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium " + ((flags.allUsersMail) ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" : "bg-gray-600 text-gray-500")}>
            AllMail
        </span>
      </div>
    );
  };

  const config = typeConfig[selectedType];

  return (
    <div className="space-y-6">
      {/* Type Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Empfehlungstyp auswählen
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(Object.keys(typeConfig) as TradingRecommendationType[]).map(type => {
            const cfg = typeConfig[type];
            const isSelected = selectedType === type;
            return (
              <button
                key={type}
                onClick={() => handleTypeChange(type)}
                className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                  isSelected
                    ? `${cfg.selectedBorder} ${cfg.selectedBg}`
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-gray-50 dark:bg-gray-700/50'
                }`}
              >
                <FontAwesomeIcon
                  icon={cfg.icon}
                  className={`w-6 h-6 mb-2 ${
                    isSelected
                      ? cfg.selectedIconText
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                />
                <span className={`text-sm font-medium ${
                  isSelected
                    ? cfg.selectedText
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {cfg.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recommendations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Tradingideen
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400 w-64">ID</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400 w-32">Ticker</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400 w-0">Gesendet</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Kommentar</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`border-b border-gray-100 dark:border-gray-700 ${
                    row.error !== null ? 'bg-red-50 dark:bg-red-900/10' : ''
                  }`}
                >
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={row.recommendationId}
                      onChange={(e) => handleRecommendationIdChange(row.id, e.target.value)}
                      placeholder="Empfehlungs-ID"
                      className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        row.error !== null
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    {row.loading ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />
                    ) : row.error !== null ? (
                      <span className="text-red-500 text-xs">{row.error}</span>
                    ) : row.recommendation !== null ? (
                      <span className="font-medium text-gray-900 dark:text-white">
                        {getTickerFromRecommendation(row.recommendation)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {row.recommendation !== null ? (
                      renderNotificationBadges(row.recommendation)
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={row.comment}
                      onChange={(e) => handleCommentChange(row.id, e.target.value)}
                      placeholder="Kommentar"
                      disabled={row.recommendation === null}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="py-2 px-3">
                    {rows.length > 1 && index < rows.length - 1 && (
                      <button
                        onClick={() => handleDeleteRow(row.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="Zeile entfernen"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          <FontAwesomeIcon icon={faPlus} className="w-3 h-3 mr-1" />
          Eine neue Zeile wird automatisch hinzugefügt, wenn Sie eine ID eingeben
        </p>
      </div>

      {/* Discord Messages */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Discord-Nachrichten
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              Pre-Message (vor allen Tradingideen)
            </label>
            <textarea
              value={discordPreMessage}
              onChange={(e) => setDiscordPreMessage(e.target.value)}
              rows={2}
              placeholder="Optionale Nachricht, die vor allen Tradingideen gesendet wird"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              Post-Message (nach allen Tradingideen)
            </label>
            <textarea
              value={discordPostMessage}
              onChange={(e) => setDiscordPostMessage(e.target.value)}
              rows={2}
              placeholder="Optionale Nachricht, die nach allen Tradingideen gesendet wird"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Notification Channels */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Benachrichtigungskanäle
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Checkbox checked={sendTelegram} onChange={setSendTelegram}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faTelegram} className="w-4 h-4 mr-1.5 text-sky-500" />
              Telegram
            </span>
          </Checkbox>

          <Checkbox checked={sendDiscord} onChange={setSendDiscord}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faDiscord} className="w-4 h-4 mr-1.5 text-indigo-500" />
              Discord
            </span>
          </Checkbox>

          <Checkbox checked={sendSCN} onChange={setSendSCN}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faBell} className="w-4 h-4 mr-1.5 text-amber-500" />
              SCN
            </span>
          </Checkbox>

          <Checkbox checked={sendPush} onChange={setSendPush}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faBell} className="w-4 h-4 mr-1.5 text-purple-500" />
              Push (App)
            </span>
          </Checkbox>

          <Checkbox checked={sendMailToFreeUser} onChange={setSendMailToFreeUser}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-1.5 text-emerald-500" />
              Mail (Free)
            </span>
          </Checkbox>

          <Checkbox checked={sendMailToAllUsers} onChange={setSendMailToAllUsers}>
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-1.5 text-emerald-500" />
              Mail (Alle)
            </span>
          </Checkbox>
        </div>
      </div>

      {/* Dry Run Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Vorschau
        </h3>
        {dryRunLoading ? (
          <div className="flex items-center justify-center py-4">
            <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin text-blue-500 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Berechne...</span>
          </div>
        ) : !isFormValid ? (
          <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
            {hasErrors
              ? 'Bitte beheben Sie die Fehler in der Tabelle'
              : 'Keine gültigen Tradingideen ausgewählt'}
          </div>
        ) : dryRunStats !== null ? (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faUsers} className="w-4 h-4 text-blue-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.receivingUsers}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Nutzer</div>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faDiscord} className="w-4 h-4 text-indigo-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.discordMessages}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Discord-Nachrichten</div>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faTelegram} className="w-4 h-4 text-sky-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.telegramMessages}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Telegram-Nachrichten</div>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faBell} className="w-4 h-4 text-amber-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.scnNotifications}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">SCN-Nachrichten</div>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faBell} className="w-4 h-4 text-purple-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.pushNotifications}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Push (App)</div>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 text-emerald-500 mr-2" />
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dryRunStats.mailNotifications}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">E-Mails</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
            Vorschau wird geladen...
          </div>
        )}
      </div>

      {/* Send Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSendClick}
          disabled={!isFormValid || isSending}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              Sende...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
              Benachrichtigungen senden
            </>
          )}
        </button>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Benachrichtigungen bereits gesendet"
        icon={<FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-amber-500" />}
        maxWidth="md"
      >
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Für folgende Tradingideen wurden bereits Benachrichtigungen gesendet.
            Sind Sie sicher, dass Sie diese erneut senden möchten?
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
              {conflictingNotifications.map((conflict, index) => (
                <li key={index}>{conflict}</li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer"
            >
              Abbrechen
            </button>
            <button
              onClick={performSend}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors cursor-pointer"
            >
              Trotzdem senden
            </button>
          </div>
        </div>
      </Modal>

      {/* Results Modal */}
      {sendResults !== null && (
        <RecommendationStatsModal
          isOpen={showResultsModal}
          onClose={handleResultsModalClose}
          stats={sendResults}
          recommendationType={selectedType.toLowerCase() as 'core' | 'earnings' | 'swing' | 'longterm'}
          text={`Benachrichtigungen für ${getValidRows().length} ${config.label}-Empfehlung(en) erfolgreich gesendet`}
        />
      )}
    </div>
  );
}
